import { CartoAPIError, QueryParameters, TilejsonResult, query } from '@carto/api-client'
import { Provider, REQUEST_GET_MAX_URL_LENGTH } from '@carto/react-core'
import { SourceFilters } from '@carto/react-redux'
import { _getSqlEscapedSource } from '@carto/react-widgets'
import { MapDatasetEnriched, GeometryType, LayerExtent } from 'features/builder/types'
import {
  AssetsResponse,
  GcsSignedUrl,
  getRequest,
  getToken,
  postRequest,
  postRequestFile,
  uploadFileRequest
} from 'features/common'
import { encodeUriParams } from 'features/common/utils/searchParamsUtils'
import {
  getColumnNameFromGeoColumn,
  getSpatialIndexFromGeoColumn,
  getGeometryFromTilesetDataset,
  Dataset
} from 'features/builder/state/builderSliceUtils'
import settings from 'settings'
import { MapCredentials, BUILDER_MOBILE_MODAL_DONOTSHOW_KEY, FetchLayerDataProps, AttributeStats } from '../types'
import { THUMBNAILS_CACHE_CONTROL } from 'features/common/utils/httpHeaderCacheControl'
import { getLocalStorageItem, setLocalStorageItem } from 'app/api/localStorage'
import { sendSQLRequest } from 'features/sql/api/sql'
import { ProviderType } from 'features/connections/types'
import { Formats, MapType } from 'features/maps/types'
import { load } from '@loaders.gl/core'
import { ImageLoader } from '@loaders.gl/images'
import { BUILTIN_MARKERS_BASE_PATH } from '../utils/makiIcons'
import { v4 as uuidv4 } from 'uuid'
import { Client, getClient } from 'features/common/utils/clientParameter'
import { uriAddSearchParams } from 'features/common/utils/navigationUtils'
import { SchemaField, SchemaFieldType } from 'features/data-explorer/types'
import { calculateLegendQuery, selectAll } from './queries/legend'
import { SQLResponse } from 'features/sql/types'
import { getCartoSource, getSourceOptions } from 'features/common/utils/cartoDeckGL'
import { DEFAULT_GEOM_COLUMN } from '../ui/builderConstants'
import { parseGeoColumn } from '../utils/geoColumn'

const { workspaceApiUrl } = settings

export async function getMapCredentials(accessToken?: string): Promise<MapCredentials> {
  return {
    accessToken: accessToken || (await getToken()),
    apiBaseUrl: settings.apiBaseUrl
  }
}

const escapeCharacter = {
  [ProviderType.BIGQUERY]: '`',
  [ProviderType.POSTGRES]: '"',
  [ProviderType.SNOWFLAKE]: '"',
  [ProviderType.REDSHIFT]: '"',
  [ProviderType.DATABRICKS]: '`',
  [ProviderType.DATABRICKS_REST]: '`'
}

export type GetLayerDataResult = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: TilejsonResult | Record<string, any>[]
  schema?: SchemaField[]
  error?: Error
}

export type GetAttributeStatsCategoryOrder =
  | 'frequency_asc'
  | 'frequency_desc'
  | 'alphabetical_asc'
  | 'alphabetical_desc'

export const getLayerData = async ({
  type,
  source,
  connection,
  provider,
  accessToken,
  format,
  geoColumn,
  columns,
  aggregationExp,
  aggregationResLevel,
  queryParameters,
  headers,
  filters,
  expectErrorWithSchema
}: FetchLayerDataProps): Promise<GetLayerDataResult | undefined> => {
  if (!connection) return

  const credentials = await getMapCredentials(accessToken)
  const sourceOptions = {
    ...credentials,
    connection,
    headers
  }

  try {
    if (format === Formats.JSON) {
      // ensure we parse-out spatialDataColumn from geoColumn which may be in canonical format
      const spatialDataColumn = (geoColumn && parseGeoColumn(geoColumn)?.spatialDataColumn) || DEFAULT_GEOM_COLUMN
      const geomAlias =
        spatialDataColumn && spatialDataColumn.toLowerCase() !== DEFAULT_GEOM_COLUMN
          ? `, ${escapeCharacter[provider]}${spatialDataColumn}${escapeCharacter[provider]} AS ${DEFAULT_GEOM_COLUMN}`
          : ''

      const { rows, schema } = await query({
        ...getSourceOptions(sourceOptions),
        sqlQuery:
          type === MapType.table
            ? `SELECT *${geomAlias} FROM ${_getSqlEscapedSource(source, provider as unknown as Provider)}`
            : source
      })

      if (geomAlias) {
        const defaultGeomColumnIndex = schema.findIndex((column) => column.name.toLowerCase() === DEFAULT_GEOM_COLUMN)
        const geoColumnIndex = schema.findIndex((column) => column.name === geoColumn)
        if (geoColumn && defaultGeomColumnIndex !== -1 && geoColumnIndex !== -1) {
          // To have no problem with snowflake and uppercase column names
          const defaultGeomColumnName = schema[defaultGeomColumnIndex].name
          rows.forEach((row) => {
            row[geoColumn] = row[defaultGeomColumnName]
            delete row[defaultGeomColumnName]
          })
          schema.splice(defaultGeomColumnIndex, 1)
        }
      }

      return {
        data: rows,
        schema: schema as SchemaField[]
      }
    }

    const data = await getCartoSource({
      ...sourceOptions,
      geoColumn,
      columns,
      type,
      source,
      queryParameters,
      aggregationExp,
      aggregationResLevel,
      filters
    })

    let schema: SchemaField[] | undefined
    if (data?.tilestats?.layers[0]) {
      schema = data.tilestats.layers[0].attributes.map((a) => ({
        name: a.attribute,
        type: a.type.toLocaleLowerCase() as SchemaFieldType
      }))
    } else if (data?.raster_bands) {
      schema = data.raster_bands.map((band) => ({ name: band, type: SchemaFieldType.Number }))
    }
    return {
      data,
      schema
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    // known responses:
    // CartoAPIError: Geometry column not found. Use the 'spatialDataColumn' parameter to specify a valid geometry column.
    if (expectErrorWithSchema && e.responseJson && Array.isArray(e.responseJson.schema)) {
      return {
        error: e,
        schema: e.responseJson.schema
      }
    }
    let msg = String(e instanceof CartoAPIError ? (e as CartoAPIError).error : (e as Error).message)
    msg = msg.replace('Unauthorized access.', '').replace('Bad request.', '')
    throw new Error(msg)
  }
}

const base64ImageToFile = (base64: string, filename: string) => {
  const [header, base64image] = base64.split(',')

  // Extract the mime type (e.g., image/png)
  const mimeType = (header.match(/:(.*?);/) || [])[1] || 'image/png'

  // Remove header and decode base64 string
  const decodedString = atob(base64image)
  let size = decodedString.length
  const u8arr = new Uint8Array(size)

  // Convert the decoded base64 string into bytes
  while (size--) {
    u8arr[size] = decodedString.charCodeAt(size)
  }

  const blob = new Blob([u8arr], { type: mimeType })

  return new File([blob], filename, { type: mimeType })
}

export const setThumbnail = async (mapId: string, base64image: string) => {
  const filename = `${mapId}.png`
  // Converting it manually instead using `fetch` to avoid requests to a base64 image (can be blocked in self-hosted environments)
  const file = base64ImageToFile(base64image, filename)

  const signPayload = {
    map_id: mapId,
    filename: filename,
    type: 'thumbnail',
    content_type: file.type
  }
  const signedUrl: GcsSignedUrl = await postRequest(`${workspaceApiUrl}/storage/sign`, signPayload)

  await uploadFileRequest({
    url: signedUrl.url,
    file,
    headers: signedUrl.headers,
    options: { cacheControl: THUMBNAILS_CACHE_CONTROL }
  })
}

export const getDryRun = async ({ connection, dataset, query, queryParameters }) => {
  const providerId = dataset?.providerId || ProviderType.BIGQUERY
  const extraParams = {
    [`${providerId}.dryRun`]: true,
    client: getClient()
  }

  return await sendSQLRequest({
    connection,
    query,
    extraParams,
    queryParameters
  })
}

export const showMobileModal = () => !getLocalStorageItem(BUILDER_MOBILE_MODAL_DONOTSHOW_KEY)

export const setMobileModalShown = () => setLocalStorageItem(BUILDER_MOBILE_MODAL_DONOTSHOW_KEY, '1')

export type GetAttributesStatsParams = {
  connection: string
  accessToken?: string
  source: string
  queryParameters?: QueryParameters | null
  attribute: string
  type: MapType
  client?: Client
  noCache?: boolean
  cacheMaxAgeInMinutes?: number
  maxNumberOfCategories?: number
  categoriesOrderBy?: GetAttributeStatsCategoryOrder
}

export const getTileLegendStats = async ({
  label,
  color,
  connection,
  source,
  sourceType,
  provider,
  queryParameters
}): Promise<SQLResponse | undefined> => {
  return sendSQLRequest({
    connection,
    extraParams: { client: getClient() },
    query: calculateLegendQuery({
      source: sourceType !== MapType.query ? selectAll({ source, provider }) : source,
      label,
      color,
      provider
    }),
    queryParameters
  })
}

export const getLayerExtent = async ({
  dataset,
  filters,
  cacheMaxAgeInMinutes
}: {
  dataset: MapDatasetEnriched
  filters?: SourceFilters
  cacheMaxAgeInMinutes?: number
}): Promise<LayerExtent | undefined> => {
  const credentials = await getMapCredentials(dataset.accessToken)
  const headers = {
    Authorization: `Bearer ${credentials.accessToken}`,
    ...(cacheMaxAgeInMinutes && { 'Cache-Control': `max-age=${cacheMaxAgeInMinutes * 60}` })
  }

  let extentUrl = `${settings.statsApiUrl}/${dataset.connectionName}`
  const client = getClient()
  const geometry = getGeometryFromTilesetDataset(dataset as Dataset)
  const spatialDataType = getSpatialIndexFromGeoColumn(dataset.geoColumn || 'unknown')
  let columnName = getColumnNameFromGeoColumn(dataset.geoColumn)

  // Snowflake requires column names to be uppercase
  if (dataset.providerId === ProviderType.SNOWFLAKE) columnName = columnName?.toUpperCase()

  if (dataset.type === MapType.query) {
    extentUrl += `/${columnName}`
    const urlWithSearchParams = uriAddSearchParams(extentUrl, {
      client,
      q: dataset.source,
      ...(dataset.queryParameters && { queryParameters: JSON.stringify(dataset.queryParameters) }),
      ...(filters && Object.keys(filters).length > 0 && { filters: JSON.stringify(filters) }),
      ...(geometry === GeometryType.Unknown && { spatialDataType })
    })

    if (urlWithSearchParams.length > REQUEST_GET_MAX_URL_LENGTH) {
      return postRequest(
        uriAddSearchParams(extentUrl, { client }),
        {
          q: dataset.source,
          queryParameters: dataset.queryParameters,
          ...(filters && Object.keys(filters).length > 0 && { filters: filters }),
          ...(geometry === GeometryType.Unknown && { spatialDataType })
        },
        { headers }
      ).then(({ extent }) => extent)
    } else {
      return getRequest(urlWithSearchParams, { headers }).then(({ extent }) => extent)
    }
  } else {
    extentUrl += `/${dataset.source}/${columnName}`

    return getRequest(
      uriAddSearchParams(extentUrl, {
        ...(geometry === GeometryType.Unknown && { spatialDataType }),
        ...(filters && Object.keys(filters).length > 0 && { filters: JSON.stringify(filters) }),
        client
      }),
      {
        headers
      }
    ).then(({ extent }) => extent)
  }
}

export const getAttributeStats = async ({
  connection,
  accessToken,
  source,
  queryParameters,
  attribute,
  type,
  client,
  maxNumberOfCategories,
  categoriesOrderBy,
  noCache,
  cacheMaxAgeInMinutes,
  ...otherOpts
}: GetAttributesStatsParams & Partial<RequestInit>): Promise<AttributeStats> => {
  const { statsApiUrl } = settings
  const credentials = await getMapCredentials(accessToken)
  let headers: Record<string, string> = { Authorization: `Bearer ${credentials.accessToken}` }
  if (cacheMaxAgeInMinutes) {
    headers = {
      ...headers,
      'Cache-Control': `max-age=${cacheMaxAgeInMinutes * 60}`
    }
  }
  if (noCache) {
    headers = {
      ...headers,
      'Cache-Control': 'no-cache'
    }
  }
  const opts = { ...otherOpts, headers }
  if (type === MapType.table) {
    return await getRequest(
      uriAddSearchParams(`${statsApiUrl}/${connection}/${source}/${attribute}`, {
        client,
        maxNumberOfCategories,
        categoriesOrderBy
      }),
      opts
    )
  }
  if (type === MapType.query) {
    const params = {
      client,
      q: source,
      queryParameters,
      maxNumberOfCategories,
      categoriesOrderBy
    }

    const url = `${statsApiUrl}/${connection}/${attribute}`
    const getUrl = `${url}?${encodeUriParams(params)}`

    if (getUrl.length > REQUEST_GET_MAX_URL_LENGTH) {
      return postRequest(url, params, opts)
    } else {
      return getRequest(getUrl, opts)
    }
  }
  throw new Error(`getAttributeStats: unsupported type ${type}`)
}

export const getCustomIcon = async ({ url }) => {
  return load(url, ImageLoader, {
    image: {
      type: 'imagebitmap'
    },
    imagebitmap: {
      resizeWidth: 16,
      resizeHeight: 16,
      resizeQuality: 'high'
    }
  })
}

function removeQueryParams(url: string) {
  const u = new URL(url)
  u.search = ''
  return u.toString()
}

export const saveCustomMarkerIcon = async (
  getParams: () => { file: File; privateBucket: boolean }
): Promise<{ url: string; id?: string }> => {
  const { file, privateBucket } = getParams()

  if (privateBucket) {
    const formData = new FormData()
    formData.append('file', file, file.name)
    formData.append('type', 'mapMarker')

    const assetsResponse: AssetsResponse = await postRequestFile<AssetsResponse>(`${workspaceApiUrl}/assets`, formData)
    return assetsResponse
  } else {
    const { basename, extension } = extractFileNameAndExtension(file.name)
    const baseFileNameEncoded = btoa(basename)

    // There is no way to convey file name as for now, so encode it in URL
    const filename = `${uuidv4()}--${baseFileNameEncoded}${extension}`

    const signPayload = {
      filename,
      type: 'marker',
      content_type: file.type
    }
    const signedUrl: GcsSignedUrl = await postRequest(`${workspaceApiUrl}/storage/sign`, signPayload)
    const resultUrl = removeQueryParams(signedUrl.url)

    await uploadFileRequest({
      url: signedUrl.url,
      file: file,
      headers: signedUrl.headers,
      options: { cacheControl: 'public, max-age=2628000' }
    })

    return { url: resultUrl }
  }
}

export function extractOriginalNameFromUrl(url: string) {
  const isFromLocalMarkersLibrary = url.startsWith(BUILTIN_MARKERS_BASE_PATH)
  let result = isFromLocalMarkersLibrary ? url : new URL(url).pathname
  const lastSlash = result.lastIndexOf('/')
  if (lastSlash !== -1) {
    result = result.substring(lastSlash + 1)
  }

  if (!isFromLocalMarkersLibrary) {
    const fileName = new URL(url).searchParams.get('fileName')
    if (fileName) {
      return fileName
    }
    // extract original file name encoded in URL, see saveCustomMarkerIcon
    const nameSeparator = result.indexOf('--')
    if (nameSeparator !== -1) {
      try {
        const { basename, extension } = extractFileNameAndExtension(result.substring(nameSeparator + 2))
        const decodedName = atob(decodeURIComponent(basename))
        result = `${decodedName}${extension}`
      } catch {
        // ignore errors when decoding name, for some strange reason we have custom
        // url with `--` not from us
      }
    }
  }

  return result
}

export function extractFileNameAndExtension(filename: string) {
  const lastSlash = filename.lastIndexOf('/')
  if (lastSlash !== -1) {
    filename = filename.substring(lastSlash + 1)
  }
  const extensionStart = filename.lastIndexOf('.')
  if (extensionStart === -1) {
    throw new Error('Invalid marker file name, no extension')
  }
  return { basename: filename.substring(0, extensionStart), extension: filename.substring(extensionStart) }
}
